/**
 * Tool metadata (chat-ui-spec §2.7 and §5.6): the "+" menu's nine pickable tools, the two rosters
 * they chain to, the verb and noun every tool's trace step uses, and when each tool can be picked.
 */

export interface ToolInfo {
  id: string;
  name: string;
  description: string;
}

export const PICKABLE_TOOLS: ToolInfo[] = [
  {
    id: 'search_history',
    name: 'Search history',
    description: 'Recall earlier turns of this chat',
  },
  {
    id: 'search_text',
    name: 'Search documents',
    description: "Find passages inside the project's files",
  },
  {
    id: 'search_drawing_text',
    name: 'Look at the drawings',
    description: 'Find sheets, details, and callouts',
  },
  {
    id: 'search_specifications_text',
    name: 'Read the specifications',
    description: 'Find clauses, requirements, and cited standards',
  },
  {
    id: 'search_images',
    name: 'Search page images',
    description: 'Find pages by what they look like',
  },
  {
    id: 'search_email_text',
    name: 'Search email',
    description: 'Find passages with sender, subject and date',
  },
  { id: 'run_sql', name: 'Run SQL', description: 'Count, list, or look up records' },
  {
    id: 'describe_table',
    name: 'Read a table definition',
    description: 'Columns, types, enum values and joins for one table',
  },
  {
    id: 'db_docs',
    name: 'Read the database notes',
    description: "CENTERLINE's own notes on what the data means",
  },
];

/** What "Look at the drawings" chains to; shown in the menu, never picked. */
export const DRAWING_ROSTER: ToolInfo[] = [
  {
    id: 'list_drawing_sheets',
    name: 'List sheets',
    description: 'Sheet numbers and titles in the set',
  },
  {
    id: 'search_drawing_text',
    name: 'Search drawings',
    description: 'Words and notes printed on the sheets',
  },
  {
    id: 'search_drawing_details',
    name: 'Search sheet structure',
    description: 'Details, sections and callouts on each sheet',
  },
  {
    id: 'search_drawing_images',
    name: 'Search drawings visually',
    description: 'Sheets that look like what you describe',
  },
  {
    id: 'get_drawing_details_json',
    name: 'Read sheet',
    description: 'Everything recorded about one sheet',
  },
  {
    id: 'trace_drawing_xrefs',
    name: 'Follow callouts',
    description: 'Where a detail or section reference leads',
  },
  { id: 'get_drawing_render', name: 'Look at sheet', description: 'An image of the whole sheet' },
  { id: 'crop_drawing_region', name: 'Zoom in', description: 'A close-up of part of a sheet' },
];

/** What "Search page images" chains to. */
export const IMAGE_ROSTER: ToolInfo[] = [
  { id: 'get_page_render', name: 'Look at page', description: 'An image of the whole page' },
  { id: 'crop_page_region', name: 'Zoom in', description: 'A close-up of part of a page' },
];

/** The roster disclosed under a pickable tool, if it has one. */
export const ROSTER_AFTER: Record<string, { label: string; tools: ToolInfo[] }> = {
  search_drawing_text: { label: 'The 8 tools it chains to', tools: DRAWING_ROSTER },
  search_images: { label: 'The 2 tools it chains to', tools: IMAGE_ROSTER },
};

const PICKABLE_IDS = new Set(PICKABLE_TOOLS.map((t) => t.id));

export const isPickable = (id: string | null | undefined): id is string =>
  !!id && PICKABLE_IDS.has(id);

const VERB_NOUN: Record<string, [string, string]> = {
  run_sql: ['Query', 'row'],
  search_text: ['Search', 'passage'],
  search_text_global: ['Search beyond selection', 'outside passage'],
  search_history: ['Recall', 'turn'],
  list_drawing_sheets: ['List sheets', 'sheet'],
  get_drawing_details_json: ['Read sheet', 'sheet'],
  trace_drawing_xrefs: ['Follow callouts', 'reference'],
  search_drawing_text: ['Search drawings', 'passage'],
  search_drawing_details: ['Search sheet structure', 'sheet'],
  search_drawing_images: ['Search drawings visually', 'sheet'],
  get_drawing_render: ['Look at sheet', 'image'],
  crop_drawing_region: ['Zoom in', 'image'],
  search_specifications_text: ['Search specifications', 'clause'],
  search_images: ['Search page images', 'page'],
  get_page_render: ['Look at page', 'image'],
  crop_page_region: ['Zoom in', 'image'],
  search_email_text: ['Search email', 'passage'],
  describe_table: ['Read table definition', 'column'],
  db_docs: ['Read database notes', 'section'],
};

/** Every tool a step can name. An unknown name is labelled as `run_sql` (§5.5). */
export const knownTool = (tool: string): string => (tool in VERB_NOUN ? tool : 'run_sql');

export const toolVerb = (tool: string): string => VERB_NOUN[knownTool(tool)][0];

export const toolNoun = (tool: string): string => VERB_NOUN[knownTool(tool)][1];

/** What the `start` frame said about a project's tools; unknown until a turn has run there. */
export interface ProjectToolFlags {
  search_text?: boolean;
  drawings?: boolean;
  specifications?: boolean;
  generic?: boolean;
}

export interface ToolGate {
  /** The active chat has a server session (so there is history to search). */
  hasSession: boolean;
  flags?: ProjectToolFlags;
  /** Global, not per project. */
  dbref?: boolean;
}

export interface ToolAvailability {
  enabled: boolean;
  /** Replaces the description when disabled. */
  reason?: string;
}

const AFTER_FIRST = 'Available after the first message in this chat';

const flagged = (value: boolean | undefined, whenFalse: string): ToolAvailability => {
  if (value === true) return { enabled: true };
  return { enabled: false, reason: value === false ? whenFalse : AFTER_FIRST };
};

export function toolAvailability(tool: string, gate: ToolGate): ToolAvailability {
  const flags = gate.flags ?? {};
  switch (tool) {
    case 'search_history':
      return gate.hasSession ? { enabled: true } : { enabled: false, reason: AFTER_FIRST };
    case 'search_text':
      // Unknown counts as available here, unlike every other lane.
      return flags.search_text === false
        ? { enabled: false, reason: 'This project has no indexed documents' }
        : { enabled: true };
    case 'search_drawing_text':
      return flagged(flags.drawings, 'This project has no ingested drawings');
    case 'search_specifications_text':
      return flagged(flags.specifications, 'This project has no ingested specifications');
    case 'search_images':
      return flagged(flags.generic, 'This project has no page images');
    case 'search_email_text':
      return flagged(flags.generic, 'Email search is not available for this project');
    case 'describe_table':
    case 'db_docs':
      return flagged(gate.dbref, 'The database reference is not enabled');
    case 'run_sql':
      return { enabled: true };
    default:
      return { enabled: false };
  }
}

/**
 * Tool glyphs as stroke paths on a 24×24 box: the nine menu glyphs from the CENTERLINE AI design, and
 * glyphs in the same style for the tools they chain to. Unknown tools draw as `run_sql`.
 */
const GLYPHS: Record<string, string[]> = {
  history: ['M4 12a8 8 0 1 0 2.3-5.6', 'M4 4v4h4', 'M12 8v4l3 2'],
  doc: ['M6 3h8l4 4v14H6z', 'M14 3v4h4', 'M9 13h6M9 17h4'],
  drawing: ['M4 20L12 4l8 16', 'M7.5 14h9', 'M12 4v3'],
  spec: ['M6 4h12v16H6z', 'M9 8h6M9 12h6', 'M9 16h4'],
  image: ['M4 5h16v14H4z', 'M4 16l5-5 4 4 3-3 4 4', 'M15 8.5a1 1 0 1 0 0.01 0'],
  email: ['M3 6h18v12H3z', 'M3 7l9 6 9-6'],
  sql: [
    'M5 6c0-1.7 3.1-3 7-3s7 1.3 7 3-3.1 3-7 3-7-1.3-7-3z',
    'M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6',
    'M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3',
  ],
  table: ['M4 5h16v14H4z', 'M4 10h16', 'M10 10v9'],
  notes: ['M7 3h10v18H7z', 'M10 7h4M10 11h4', 'M10 15h2'],
  globe: [
    'M12 3a9 9 0 1 0 0.01 0z',
    'M3 12h18',
    'M12 3c2.4 2.5 3.6 5.5 3.6 9s-1.2 6.5-3.6 9c-2.4-2.5-3.6-5.5-3.6-9S9.6 5.5 12 3z',
  ],
  sheets: ['M9 6h11M9 12h11M9 18h11', 'M4 6h1M4 12h1M4 18h1'],
  json: [
    'M9 4H8a2 2 0 0 0-2 2v3l-2 3 2 3v3a2 2 0 0 0 2 2h1',
    'M15 4h1a2 2 0 0 1 2 2v3l2 3-2 3v3a2 2 0 0 1-2 2h-1',
  ],
  xref: ['M7 4a3 3 0 1 0 0.01 0z', 'M9.2 9.2L17 17', 'M12 17h5v-5'],
  structure: ['M4 4h7v5H4z', 'M13 15h7v5h-7z', 'M7.5 9v8.5H13'],
  visual: ['M3 5h12v10H3z', 'M18 13a3 3 0 1 0 0.01 0z', 'M20.2 18.2L22 20'],
  look: ['M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z', 'M12 9a3 3 0 1 0 0.01 0z'],
  zoom: ['M10 4a6 6 0 1 0 0.01 0z', 'M20 20l-5.6-5.6', 'M10 7v6M7 10h6'],
};

const TOOL_GLYPH: Record<string, string> = {
  search_history: 'history',
  search_text: 'doc',
  search_text_global: 'globe',
  search_drawing_text: 'drawing',
  search_specifications_text: 'spec',
  search_images: 'image',
  search_email_text: 'email',
  run_sql: 'sql',
  describe_table: 'table',
  db_docs: 'notes',
  list_drawing_sheets: 'sheets',
  get_drawing_details_json: 'json',
  trace_drawing_xrefs: 'xref',
  search_drawing_details: 'structure',
  search_drawing_images: 'visual',
  get_drawing_render: 'look',
  crop_drawing_region: 'zoom',
  get_page_render: 'look',
  crop_page_region: 'zoom',
};

/** The stroke paths for a tool's glyph. */
export const toolIconPaths = (tool: string): string[] =>
  GLYPHS[TOOL_GLYPH[knownTool(tool)]] ?? GLYPHS.sql;
